import React from 'react';
import type { LayoutComponent as LayoutComponentType } from '../../types/layout';
import CommercialCard from '../../components/CommercialCard/CommercialCard';
import RecreationalCard from '../../components/RecreationalCard/RecreationalCard';
import UtilizationCard from '../../components/UtilizationCard/UtilizationCard';
import AbcOflLandingsCard from '../../components/AbcOflLandingsCard/AbcOflLandingsCard';
import RevenueCard from '../../components/RevenueCard/RevenueCard';
import VesselsCard from '../../components/VesselsCard/VesselsCard';
import KeyPortsCardRegistry from '../../components/KeyPortsCard/KeyPortsCardRegistry';
import ProjectionMortalityStrategy from '../../components/ProjectionMortalityStrategy/ProjectionMortalityStrategy';
import GroundfishNaturalMortality from '../../components/GroundfishNaturalMortality/GroundfishNaturalMortality';
import AbcControlRule from '../../components/AbcControlRule';
import MgmtConfidence from '../../components/MgmtConfidence';
import ScallopNaturalMortalityGB from '../../components/ScallopNaturalMortalityGB';
import ScallopNaturalMortalityMA from '../../components/ScallopNaturalMortalityMA';
import BasScallopNaturalMortality from '../../components/BasScallopNaturalMortality';
import BasScallopWeight from '../../components/BasScallopWeight';
import DaysAtSea from '../../components/DaysAtSea';
import FleetSize from '../../components/FleetSize/FleetSize';
import LpueCalcMethod from '../../components/LpueCalcMethod/LpueCalcMethod';
import AreaAccessConfig from '../../components/AreaAccessConfig/AreaAccessConfig';
import TimePeriod from '../../components/TimePeriod/TimePeriod';
import LandedExVesselVal from '../../components/LandedExVesselVal/LandedExVesselVal';
import LobsterExVessel from '../../components/LobsterExVessel/LobsterExVessel';
import LobsterHarvesting from '../../components/LobsterHarvesting/LobsterHarvesting';
import LobsterProcessing from '../../components/LobsterProcessing/LobsterProcessing';
import LobsterLandings from '../../components/LobsterLandings/LobsterLandings';
import LekSpeciesInfo from '../../components/LekSpeciesInfo/LekSpeciesInfo';
import MissedRevenueToFishermen from '../../components/MissedRevenueToFishermen/MissedRevenueToFishermen';
import LandedEconomicImpact from '../../components/LandedEconomicImpact/LandedEconomicImpact';
import MissedEconomicImpact from '../../components/MissedEconomicImpact/MissedEconomicImpact';
import JobsImpact from '../../components/JobsImpact/JobsImpact';
import LandedMeals from '../../components/LandedMeals/LandedMeals';
import TradeDeficit from '../../components/TradeDeficit/TradeDeficit';
import StockAssessmentMetrics from '../../components/StockAssessmentMetrics/StockAssessmentMetrics';
import StockAssessmentReport from '../../components/StockAssessmentReport/StockAssessmentReport';
import RiskPolicyMatrix from '../../components/RiskPolicyMatrix/RiskPolicyMatrix';
import ChokeStockAnalysis from '../../components/ChokeStockAnalysis/ChokeStockAnalysis';
import SamsManagementAreasFilter from '../../components/SamsManagementAreasFilter/SamsManagementAreasFilter';
import SubmitGroundfishProjection from '../../components/SubmitGroundfishProjection/SubmitGroundfishProjection';
import SubmitWhamProjection from '../../components/SubmitWhamProjection/SubmitWhamProjection';
import SubmitSamsProjection from '../../components/SubmitSamsProjection/SubmitSamsProjection';
import SubmitLobsterProjection from '../../components/SubmitLobsterProjection/SubmitLobsterProjection';
import ResetGroundfishProjection from '../../components/ResetGroundfishProjection/ResetGroundfishProjection';
import ResetSamsProjection from '../../components/ResetSamsProjection/ResetSamsProjection';
import ResetLobsterProjection from '../../components/ResetLobsterProjection/ResetLobsterProjection';
import LogInputs from '../../components/LogInputs/LogInputs';
import LogStream from '../../components/LogStream/LogStream';
import SamsTotalExploitableBiomass from '../../components/SamsTotalExploitableBiomass/SamsTotalExploitableBiomass';
import SamsFleetAvgF from '../../components/SamsFleetAvgF/SamsFleetAvgF';
import HerringSupply from '../../components/HerringSupply/HerringSupply';
import MenhadenSupply from '../../components/MenhadenSupply/MenhadenSupply';
import BaitImportUnitValue from '../../components/BaitImportUnitValue';
import FuelPrice from '../../components/FuelPrice';
import TrapPrice from '../../components/TrapPrice';
import RopePrice from '../../components/RopePrice';
import RopelessGearTechnology from '../../components/RopelessGearTechnology';
import LobsterFisheryEconomics from '../../components/LobsterFisheryEconomics';
import EndogenousBaitMarketPrices from '../../components/EndogenousBaitMarketPrices';
import RockyBottomTypeDataLayer from '../../features/mapping/dataLayers/RockyBottomTypeDataLayer';
import EnhancedBathymetryDataLayer from '../../features/mapping/dataLayers/EnhancedBathymetryDataLayer';
import NoaaFishingClosedAreasDataLayer from '../../features/mapping/dataLayers/NoaaFishingClosedAreasDataLayer';
import ScallopAreasDataLayer from '../../features/mapping/dataLayers/ScallopAreasDataLayer';
import LobsterManagementAreasDataLayer from '../../features/mapping/dataLayers/LobsterManagementAreasDataLayer';
import InternationalFisheryMgmtAreasDataLayer from '../../features/mapping/dataLayers/InternationalFisheryMgmtAreasDataLayer';
import UsRegionalFisheryMgmtCouncilsDataLayer from '../../features/mapping/dataLayers/UsRegionalFisheryMgmtCouncilsDataLayer';
import UsCongressionalDistrictsDataLayer from '../../features/mapping/dataLayers/UsCongressionalDistrictsDataLayer';
import NoaaNavigationChartsDataLayer from '../../features/mapping/dataLayers/NoaaNavigationChartsDataLayer';
import FishingPortsDataLayer from '../../features/mapping/dataLayers/FishingPortsDataLayer';
import ColdStorageDataLayer from '../../features/mapping/dataLayers/ColdStorageDataLayer';
import PortsWithLandingDataLayer from '../../features/mapping/dataLayers/PortsWithLandingDataLayer';
import BottomTemperatureDataLayer from '../../features/mapping/dataLayers/BottomTemperatureDataLayer';
import StateBoundariesDataLayer from '../../features/mapping/dataLayers/StateBoundariesDataLayer';
import LekHabitatDataLayer from '../../features/mapping/dataLayers/LekHabitatDataLayer';
import TrawlSurveyDataLayer from '../../features/mapping/dataLayers/TrawlSurveyDataLayer';
import VesselTrackingDataLayer from '../../features/mapping/dataLayers/VesselTrackingDataLayer';
import PlayScallopSimulationDataLayer from '../../features/mapping/dataLayers/PlayScallopSimulationDataLayer';
import PlayGroundfishSimulationDataLayer from '../../features/mapping/dataLayers/PlayGroundfishSimulationDataLayer';
import FishMgmtAdjustmentsText from '../../components/FishMgmtAdjustmentsText/FishMgmtAdjustmentsText';

/** Data layer components accept RegistryComponentProps; cast for registry. */
const RockyBottomTypeDataLayerRegistry = RockyBottomTypeDataLayer as RegistryComponent;
const EnhancedBathymetryDataLayerRegistry = EnhancedBathymetryDataLayer as RegistryComponent;
const NoaaFishingClosedAreasDataLayerRegistry = NoaaFishingClosedAreasDataLayer as RegistryComponent;
const ScallopAreasDataLayerRegistry = ScallopAreasDataLayer as RegistryComponent;
const LobsterManagementAreasDataLayerRegistry = LobsterManagementAreasDataLayer as RegistryComponent;
const InternationalFisheryMgmtAreasDataLayerRegistry = InternationalFisheryMgmtAreasDataLayer as RegistryComponent;
const UsRegionalFisheryMgmtCouncilsDataLayerRegistry = UsRegionalFisheryMgmtCouncilsDataLayer as RegistryComponent;
const UsCongressionalDistrictsDataLayerRegistry = UsCongressionalDistrictsDataLayer as RegistryComponent;
const NoaaNavigationChartsDataLayerRegistry = NoaaNavigationChartsDataLayer as RegistryComponent;
const FishingPortsDataLayerRegistry = FishingPortsDataLayer as RegistryComponent;
const ColdStorageDataLayerRegistry = ColdStorageDataLayer as RegistryComponent;
const PortsWithLandingDataLayerRegistry = PortsWithLandingDataLayer as RegistryComponent;
const BottomTemperatureDataLayerRegistry = BottomTemperatureDataLayer as RegistryComponent;
const StateBoundariesDataLayerRegistry = StateBoundariesDataLayer as RegistryComponent;
const LekHabitatDataLayerRegistry = LekHabitatDataLayer as RegistryComponent;
const TrawlSurveyDataLayerRegistry = TrawlSurveyDataLayer as RegistryComponent;
const VesselTrackingDataLayerRegistry = VesselTrackingDataLayer as RegistryComponent;
const PlayScallopSimulationDataLayerRegistry = PlayScallopSimulationDataLayer as RegistryComponent;
const PlayGroundfishSimulationDataLayerRegistry = PlayGroundfishSimulationDataLayer as RegistryComponent;

/**
 * All component slugs supported by the layout-driven rendering system.
 * Registry maps each slug to its React component.
 */
export const COMPONENT_SLUGS = [
  // Left panel - sliders & controls
  'projection-mortality-strategy',
  'groundfish-natural-mortality',
  'abc-control-rule',
  'mgmt-confidence',
  'herring-2026-supply',
  'herring-2027-supply',
  'herring-2028-supply',
  'herring-supply',
  'menhaden-supply',
  'menhaden-2026-supply',
  'menhaden-2027-supply',
  'menhaden-2028-supply',
  'bait-import-unit-value',
  'fuel-price',
  'trap-price',
  'rope-price',
  'ropeless-gear-technology',
  'scallop-natural-mortality-gb',
  'scallop-natural-mortality-ma',
  'bas-scallop-natural-mortality',
  'bas-scallop-weight',
  'days-at-sea',
  'fleet-size',
  'lpue-calc-method',
  'area-access-config',
  'time-period',
  'submit-groundfish-projection',
  'submit-wham-projection',
  'submit-sams-projection',
  'submit-lobster-projection',
  'reset-groundfish-projection',
  'reset-sams-projection',
  'reset-lobster-projection',
  'log-inputs',
  'log-stream',
  // Right panel - insight cards
  'lek-species-info',
  'sams-total-exploitable-biomass',
  'sams-fleet-avg-f',
  'landed-ex-vessel-val',
  'lobster-ex-vessel',
  'lobster-harvesting',
  'lobster-processing',
  'lobster-landings',
  'jobs-impact',
  'landed-meals',
  'missed-revenue-to-fishermen',
  'landed-economic-impact',
  'missed-economic-impact',
  'trade-deficit',
  'stock-assessment-metrics',
  'stock-assessment-report',
  'risk-policy-matrix',
  'choke-stock-analysis',
  'lobster-fishery-economics',
  'endogenous-bait-market-prices',
  'commercial-fishery-stats',
  'recreational-fishery-stats',
  'utilization',
  'abc-ofl-landings',
  'revenue',
  'vessels',
  'key-ports',
  'total-ex-vessel-val',
  'harvesting-stats',
  'processing-stats',
  'landings',
  // Data layers
  'rocky-bottom-type',
  'enhanced-bathymetry',
  'noaa-fishing-closed-areas',
  'scallop-areas',
  'lobster-management-areas',
  'international-fishery-mgmt-areas',
  'us-regional-fishery-mgmt-councils',
  'us-congressional-districts',
  'noaa-navigation-charts',
  'fishing-ports',
  'cold-storage',
  'ports-with-landing',
  'bottom-temperature',
  'state-boundaries',
  'lek',
  'trawl-survey',
  // Other
  'play-groundfish-simulation',
  'play-scallop-simulation',
  'vessel-tracking',
  'sams-management-areas-filter',
  'fish-mgmt-adjustments-text',
] as const;

/** Slugs that represent map data layers (bottom panel). Used for layout active-data-layers and edit mode. */
export const DATA_LAYER_SLUGS = [
  'rocky-bottom-type',
  'enhanced-bathymetry',
  'noaa-fishing-closed-areas',
  'scallop-areas',
  'lobster-management-areas',
  'international-fishery-mgmt-areas',
  'us-regional-fishery-mgmt-councils',
  'us-congressional-districts',
  'noaa-navigation-charts',
  'fishing-ports',
  'cold-storage',
  'ports-with-landing',
  'bottom-temperature',
  'state-boundaries',
  'lek',
  'trawl-survey',
  'vessel-tracking',
] as const;

export type DataLayerSlug = (typeof DATA_LAYER_SLUGS)[number];

export type ComponentSlug = (typeof COMPONENT_SLUGS)[number];

/** Human-readable label for a slug (e.g. "projection-mortality-strategy" → "Projection Mortality Strategy") */
export function slugToLabel(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Base props passed to all registry components when rendered dynamically.
 * Components may extend this with their own required props via componentInjections.
 */
export interface RegistryComponentProps {
  componentSlug: ComponentSlug;
  componentName?: string;
  defaultValues?: Record<string, unknown> | null;
  /** Page-specific data injections (e.g. insights, stockId) - passed by the page */
  injections?: Record<string, unknown>;
}

export type RegistryComponent = React.ComponentType<RegistryComponentProps>;

/**
 * Extracts merged data from defaultValues and injections for type-safe use in registry components.
 */
export function getRegistryData<T extends object = Record<string, unknown>>(
  props: RegistryComponentProps
): T {
  return { ...props.defaultValues, ...props.injections } as T;
}

/**
 * Fallback component for slugs not yet implemented.
 * Renders a dev placeholder so layout-driven rendering never fails silently.
 */
export function FallbackComponent({ componentSlug }: RegistryComponentProps): React.ReactElement {
  return (
    <div
      style={{
        padding: '12px',
        margin: '8px 0',
        backgroundColor: '#fef3c7',
        border: '1px dashed #d97706',
        borderRadius: '4px',
        fontSize: '13px',
        color: '#92400e',
      }}
    >
      Component <code>{componentSlug}</code> not yet implemented
    </div>
  );
}

/**
 * Component registry: maps each slug to its React component.
 * Replace FallbackComponent with actual components as they are extracted.
 */
const REGISTRY: Record<ComponentSlug, RegistryComponent> = {
  'projection-mortality-strategy': ProjectionMortalityStrategy,
  'groundfish-natural-mortality': GroundfishNaturalMortality,
  'abc-control-rule': AbcControlRule,
  'mgmt-confidence': MgmtConfidence,
  'herring-2026-supply': FallbackComponent,
  'herring-2027-supply': FallbackComponent,
  'herring-2028-supply': FallbackComponent,
  'herring-supply': HerringSupply,
  'menhaden-supply': MenhadenSupply,
  'menhaden-2026-supply': FallbackComponent,
  'menhaden-2027-supply': FallbackComponent,
  'menhaden-2028-supply': FallbackComponent,
  'bait-import-unit-value': BaitImportUnitValue,
  'fuel-price': FuelPrice,
  'trap-price': TrapPrice,
  'rope-price': RopePrice,
  'ropeless-gear-technology': RopelessGearTechnology,
  'scallop-natural-mortality-gb': ScallopNaturalMortalityGB,
  'scallop-natural-mortality-ma': ScallopNaturalMortalityMA,
  'bas-scallop-natural-mortality': BasScallopNaturalMortality,
  'bas-scallop-weight': BasScallopWeight,
  'days-at-sea': DaysAtSea,
  'fleet-size': FleetSize,
  'lpue-calc-method': LpueCalcMethod,
  'area-access-config': AreaAccessConfig,
  'time-period': TimePeriod,
  'submit-groundfish-projection': SubmitGroundfishProjection,
  'submit-wham-projection': SubmitWhamProjection,
  'submit-sams-projection': SubmitSamsProjection,
  'submit-lobster-projection': SubmitLobsterProjection,
  'reset-groundfish-projection': ResetGroundfishProjection,
  'reset-sams-projection': ResetSamsProjection,
  'reset-lobster-projection': ResetLobsterProjection,
  'log-inputs': LogInputs,
  'log-stream': LogStream,
  'lek-species-info': LekSpeciesInfo,
  'sams-total-exploitable-biomass': SamsTotalExploitableBiomass,
  'sams-fleet-avg-f': SamsFleetAvgF,
  'landed-ex-vessel-val': LandedExVesselVal,
  'lobster-ex-vessel': LobsterExVessel,
  'lobster-harvesting': LobsterHarvesting,
  'lobster-processing': LobsterProcessing,
  'lobster-landings': LobsterLandings,
  'jobs-impact': JobsImpact,
  'landed-meals': LandedMeals,
  'missed-revenue-to-fishermen': MissedRevenueToFishermen,
  'landed-economic-impact': LandedEconomicImpact,
  'missed-economic-impact': MissedEconomicImpact,
  'trade-deficit': TradeDeficit,
  'stock-assessment-metrics': StockAssessmentMetrics,
  'stock-assessment-report': StockAssessmentReport,
  'risk-policy-matrix': RiskPolicyMatrix,
  'choke-stock-analysis': ChokeStockAnalysis,
  'lobster-fishery-economics': LobsterFisheryEconomics,
  'endogenous-bait-market-prices': EndogenousBaitMarketPrices,
  'commercial-fishery-stats': CommercialCard,
  'recreational-fishery-stats': RecreationalCard,
  'utilization': UtilizationCard,
  'abc-ofl-landings': AbcOflLandingsCard,
  'revenue': RevenueCard,
  'vessels': VesselsCard,
  'key-ports': KeyPortsCardRegistry,
  'total-ex-vessel-val': FallbackComponent,
  'harvesting-stats': FallbackComponent,
  'processing-stats': FallbackComponent,
  'landings': FallbackComponent,
  'rocky-bottom-type': RockyBottomTypeDataLayerRegistry,
  'enhanced-bathymetry': EnhancedBathymetryDataLayerRegistry,
  'noaa-fishing-closed-areas': NoaaFishingClosedAreasDataLayerRegistry,
  'scallop-areas': ScallopAreasDataLayerRegistry,
  'lobster-management-areas': LobsterManagementAreasDataLayerRegistry,
  'international-fishery-mgmt-areas': InternationalFisheryMgmtAreasDataLayerRegistry,
  'us-regional-fishery-mgmt-councils': UsRegionalFisheryMgmtCouncilsDataLayerRegistry,
  'us-congressional-districts': UsCongressionalDistrictsDataLayerRegistry,
  'noaa-navigation-charts': NoaaNavigationChartsDataLayerRegistry,
  'fishing-ports': FishingPortsDataLayerRegistry,
  'cold-storage': ColdStorageDataLayerRegistry,
  'ports-with-landing': PortsWithLandingDataLayerRegistry,
  'bottom-temperature': BottomTemperatureDataLayerRegistry,
  'state-boundaries': StateBoundariesDataLayerRegistry,
  'lek': LekHabitatDataLayerRegistry,
  'trawl-survey': TrawlSurveyDataLayerRegistry,
  'play-groundfish-simulation': PlayGroundfishSimulationDataLayerRegistry,
  'play-scallop-simulation': PlayScallopSimulationDataLayerRegistry,
  'vessel-tracking': VesselTrackingDataLayerRegistry,
  'sams-management-areas-filter': SamsManagementAreasFilter,
  'fish-mgmt-adjustments-text': FishMgmtAdjustmentsText,
};

/**
 * Returns the component for a given slug, or FallbackComponent if slug is unknown.
 */
export function getComponent(slug: string): RegistryComponent {
  if (isComponentSlug(slug)) {
    return REGISTRY[slug];
  }
  return FallbackComponent;
}

/**
 * Type guard to check if a string is a valid ComponentSlug.
 */
export function isComponentSlug(slug: string): slug is ComponentSlug {
  return (COMPONENT_SLUGS as readonly string[]).includes(slug);
}

/**
 * Registers (or overwrites) a component for a slug.
 * Useful for testing or gradual migration.
 */
export function registerComponent(slug: ComponentSlug, component: RegistryComponent): void {
  REGISTRY[slug] = component;
}

export interface DynamicComponentProps {
  component: LayoutComponentType;
  /** Page-specific data to inject (e.g. insights, stockId, modelType) */
  injections?: Record<string, unknown>;
}

/**
 * Renders a layout component by looking up its slug in the registry.
 * Used for dynamic panel content driven by the layout API.
 */
export function DynamicComponent({ component, injections }: DynamicComponentProps): React.ReactElement {
  const Comp = getComponent(component.component_slug);
  const props: RegistryComponentProps = {
    componentSlug: component.component_slug as ComponentSlug,
    componentName: component.component_name,
    defaultValues: component.component_default_values,
    injections,
  };
  return <Comp {...props} />;
}
